import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { VerticalMenu } from '@/components/shared/menu/VerticalMenu';
import { useLocation } from '@/services/useLocations';
import { FC } from 'react';
import { FiClock } from 'react-icons/fi';
import { GiCog } from 'react-icons/gi';
import { useNavigate, useParams } from 'react-router-dom';
import strings from '../../admin.translations';
import { deactivateReactivateTabConfig } from '../../common';
import AdminDeactivateReactivateLocationCard from './AdminDeactivateReactivateLocationCard';
import AdminEditLocationDetailsCard from './AdminEditLocationDetailsCard';
import AdminEditLocationTimesCard from './AdminEditLocationTimesCard';

export const AdminEditLocationPage: FC = () => {
  const { tabName, locationId } = useParams();

  if (!locationId) throw new Error('Missing location id');

  const navigate = useNavigate();

  const { location, refetch } = useLocation(parseInt(locationId));

  const breadcrumbs: NavPage[] = [
    {
      name: strings.page.admin,
      to: '/admin'
    },
    {
      name: strings.locationsSection.editLocation.locations,
      to: '/admin/locations'
    },
    {
      name: location.name,
      to: `/admin/locations/${locationId}`
    }
  ];

  const { icon: DeactivateReactivateIcon, label: deactivateReactivateLabel } =
    deactivateReactivateTabConfig[location.active ? 'active' : 'inactive'];

  const sideMenuOptions = [
    {
      icon: <GiCog className="h-5 w-5" />,
      label: strings.locationsSection.editLocation.locationDetails,
      anchorId: 'edit-location',
      element: <AdminEditLocationDetailsCard location={location} onUpdated={refetch} />
    },
    {
      icon: <FiClock className="h-5 w-5" />,
      label: strings.locationsSection.editLocationTimes.defaultTimes,
      anchorId: 'edit-default-times',
      element: <AdminEditLocationTimesCard location={location} />
    },
    {
      icon: <DeactivateReactivateIcon className="h-5 w-5" />,
      label: deactivateReactivateLabel,
      anchorId: 'deactivate-reactivate',
      element: <AdminDeactivateReactivateLocationCard location={location} />
    }
  ];

  const allElements = (
    <>
      <AdminEditLocationDetailsCard location={location} onUpdated={refetch} />
      <AdminEditLocationTimesCard location={location} />
      <AdminDeactivateReactivateLocationCard location={location} />
    </>
  );

  const selectedTab = tabName ?? sideMenuOptions[0].anchorId;
  const handleTabSelect = (anchorId: string) => {
    navigate(`/admin/locations/${locationId}/${anchorId}`);
  };

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.page.locationTitle} icon={<FiClock />} breadcrumbs={breadcrumbs} />}
    >
      <PageSizer className="flex gap-5 flex-col lg:flex-row" mode={SizerMode.PAGE}>
        <div className="hidden lg:flex gap-5 w-full">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            isScroll={false}
            selectedTab={selectedTab}
          />
        </div>
        <div className="lg:hidden">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            allElements={allElements}
            isScroll={true}
            selectedTab={selectedTab}
          />
        </div>
      </PageSizer>
    </PageContainer>
  );
};
